/**
 * AI:
 * Status: 'in progress'
 * Type: Logic
 * SubType: Ql
 * Reason: A-1 - the Benachrichtigungen tab of the signing-process OBC. ONE QL query over
 *         ns.NsNotificationChannelRecipient, bound to the widget alias
 *         `sm.signing.process.object.notifications` via WidgetFactory.createWidgetTableQl.
 *
 * <h2>Why the RECIPIENT is the root and not the notification</h2>
 * `ns.NsNotificationChannelRecipient` is the one table that carries the whole row: the polymorphic
 * anchor (`objectType` + `objectId`, set to the SIGNER by
 * `NsNotificationSendInternalRecipientChannelService:235-237`, where `NsRecipient.getId()` returns
 * the entity's own id), the delivery result (`recipientStatus`, `channelRecipientAddress`,
 * `errorMessage`) and the rendered mail (`notificationContent` -> `document`). Starting at
 * `ns.NsNotification` would need a reverse join per row and would still have to come back here for
 * the anchor.
 *
 * <h2>The anchor is matched by ALIAS, never by id</h2>
 * `ot.alias = 'sm.SmSigningProcessSigner'`. The numeric `object_type_id` (1271 on dev/tenantA) is an
 * environment fact, not a contract: `cc.ObjectType` rows are per-tenant-database and the id differs
 * between development, clone and production. Filtering on the id would produce an EMPTY tab
 * everywhere but on the environment it was written against - and an empty tab is indistinguishable
 * from a process where nothing was sent. Same reason no notification-type id (195/196 on dev)
 * appears here.
 *
 * <h2>What this tab CANNOT show - stated, not hidden</h2>
 * Caseworker notifications are absent by construction and this is a signed descope (capability
 * matrix, descope 4). `SmSigningInvitationService.recordRefusedDispatch:419-459` sends the refusal
 * report to `caseworker.recipient()`, so its anchor is a `um.User`, never an
 * `sm.SmSigningProcessSigner`, and no filter over this table can reach it. Those events appear in the
 * Verlauf as audit events (`dispatchRefused`, `dispatchRetryFailed`, `originNotCheckable`,
 * `notificationSent`) instead. The tab must carry a visible German pointer saying so.
 *
 * <h2>The signer NAME is not obtainable from this query - by construction</h2>
 * `objectId` is a plain `Long` column, not a foreign key: there is no `signer` relationship on this
 * entity, so no QL join can reach `sm.SmSigningProcessSigner.signerName`. Do not „fix“ this by
 * adding a join; the compiler has nothing to join on.
 *
 * <b>Nor can the name be injected on the widget side.</b> Verified in the platform, not assumed:
 * <ul>
 *   <li>`widget.service.ts:331,339,341-366` clones the BACKEND `MvsFormFieldDto` for rendering and
 *       copies exactly nine properties over from the caller's `WidgetConfigFormField` -
 *       `valueList` is NOT one of them. `widget.service.ts:471` then looks the cell label up with
 *       `MvsFormFieldDto.lookupValueListEntry(formField, ...)`, i.e. against the backend field. A
 *       value list a caller sets on a `WidgetConfigFormField` therefore never reaches a cell.</li>
 *   <li>No `uiOutputControl` output field resolves an `objectType` + `objectId` pair.
 *       `MvsNavigateToNewWindowOutputComponent` needs a STATIC `uiConfigData.objectType`;
 *       `MvsObjectInlineComponent` does resolve the pair but is not an output field and is not
 *       registered in `MvsFormFieldService.fieldGroupOutputComponents`.</li>
 *   <li>`mvs-widget-table` has no per-row polymorphic handling, and the in-tree QL tables over this
 *       very entity (`ns-dashboard-worklist.widget.ts`, the `epost` worklist; `ns-notification-object.component.ts:233`)
 *       all render `objectId` raw.</li>
 * </ul>
 * So the column below shows the signer REFERENCE. The two sanctioned seams for turning it into a
 * name, should the OBC want one, are the widget custom-field template
 * (`mvs-widget-custom-field.directive.ts`, which receives the whole row) hosting
 * `<mvs-object-inline [objectTypeId] [objectId]>`, or a new registered output field - both are the
 * component specialist's call, not this file's.
 *
 * <h2>Three measured join traps, taken verbatim from `buildRecipientWorklistQuery`</h2>
 * (`ns-dashboard-overview.component.ts:845-904`, TM-1000001984 - all three were paid for once):
 * <ol>
 *   <li><b>Every join needs `joinType: 'left'`, lowercase.</b> An omitted `joinType` compiles to
 *       INNER and silently drops every recipient whose content, channel or type row is absent.</li>
 *   <li><b>`ns.NsNotificationChannel` has NO audit columns</b> - it extends `EntityBase`, not
 *       `AuditableEntity`. There is no `createdDate` on it to sort by; the timestamp of the row is
 *       `n.createdDate` on `ns.NsNotification`, which extends `AuditableActivityEntity`.</li>
 *   <li><b>Every join needs a NON-EMPTY `fields` list</b>, including a pure hop such as `ctt`. An
 *       entity join without one projects the WHOLE entity, and because the layout the framework
 *       derives from the projection OUTRANKS a curated `widgetForm`, the extra attributes reach the
 *       table as columns. `fields: []` does NOT suppress it - the list must be non-empty.</li>
 * </ol>
 *
 * The query literal below is kept COMPACT on purpose: the QL semantic validator reads each query
 * through a flat 3000-character window starting at `start:`, and a long comment inside the object
 * pushes the closing bracket out of range, after which every alias but `e` is reported unknown.
 */

import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';

/**
 * Widget alias of the „Benachrichtigungen“ tab. Pattern `module.feature.entity.type.variant`.
 */
export const SM_SIGNING_PROCESS_NOTIFICATIONS_ALIAS = 'sm.signing.process.object.notifications';

/**
 * Query name AND pipe name. `QlWidgetFactory.createWidgetRequest` derives the pipe from the query
 * name, and results are read back by PIPE name - the two must not drift apart.
 */
export const SM_SIGNING_PROCESS_NOTIFICATIONS_QUERY = 'smSigningProcessNotifications';

/**
 * The `cc.ObjectType` alias of a signer row. The ONLY admissible way to address the polymorphic
 * anchor - see the class comment on why the numeric id is forbidden.
 */
export const SM_SIGNER_OBJECT_TYPE_ALIAS = 'sm.SmSigningProcessSigner';

/**
 * The signer-addressed notifications of ONE signing process, newest first.
 *
 * @param signerIds ids of every `sm.SmSigningProcessSigner` of the process. An EMPTY array is a
 *                  legitimate input and yields zero rows (SQL `IN ()` is never true) - the caller
 *                  renders its empty state and must not skip the query, so that the loading and
 *                  error states stay on one code path.
 */
export function smSigningProcessNotificationsQuery(signerIds: number[]): QlQueryDto {
    return {
        name: SM_SIGNING_PROCESS_NOTIFICATIONS_QUERY,
        type: 'view',
        start: {
            type: 'entity',
            name: 'ns.NsNotificationChannelRecipient',
            as: 'e',
            fields: [{name: 'id'}, {name: 'objectId'}, {name: 'recipientStatus'},
                {name: 'channelRecipientAddress'}, {name: 'errorMessage'}],
            joins: [
                {type: 'entity', joinType: 'left', name: 'objectType', as: 'ot',
                    fields: [{name: 'id'}, {name: 'alias'}]},
                {type: 'entity', joinType: 'left', name: 'notificationChannel', as: 'nc',
                    fields: [{name: 'id'}, {name: 'channelStatus'}],
                    joins: [
                        {type: 'entity', joinType: 'left', name: 'notification', as: 'n',
                            fields: [{name: 'id'}, {name: 'createdDate'}, {name: 'notificationStatus'}],
                            joins: [
                                {type: 'entity', joinType: 'left', name: 'notificationType', as: 'nt',
                                    fields: [{name: 'id'}, {name: 'name'}]}
                            ]},
                        {type: 'entity', joinType: 'left', name: 'channelType', as: 'ctt',
                            fields: [{name: 'id'}],
                            joins: [
                                {type: 'entity', joinType: 'left', name: 'channelType', as: 'cct',
                                    fields: [{name: 'id'}, {name: 'name'}]}
                            ]}
                    ]},
                {type: 'entity', joinType: 'left', name: 'notificationContent', as: 'nco',
                    fields: [{name: 'id'}, {name: 'subject'}],
                    joins: [
                        {type: 'entity', joinType: 'left', name: 'document', as: 'doc',
                            fields: [{name: 'id'}, {name: 'name'}]}
                    ]}
            ]
        },
        fixedFilters: [
            FilterCriteria.create('ot.alias', FilterCriteria.cOperatorEqual, SM_SIGNER_OBJECT_TYPE_ALIAS),
            FilterCriteria.create('e.objectId', FilterCriteria.cOperatorIn, signerIds)
        ],
        sortings: [new Sorting('n.createdDate', false)]
    };
}

/**
 * The curated column set of the tab, in display order.
 *
 * The `field` values ARE the QL result keys (`<alias>.<field>`) that
 * `smSigningProcessNotificationsQuery` projects. A key that matches no projection is skipped
 * SILENTLY - no error, just a missing column - so this list and the projections above must be kept
 * in step.
 *
 * `doc.id` is deliberately absent: it is projected for the row click that opens the rendered mail via
 * `DmDocumentService.openInBrowserTab` (FD-6, „display“ rather than „link“), and a column carrying a
 * raw document id would say nothing to a caseworker. `e.id` and `n.id` are carried for the same
 * reason.
 *
 * `e.recipientStatus` and `n.notificationStatus` are enum ORDINALS on the wire. They render as their
 * German labels without any mapping here because `QlWidgetFactory.createWidgetRequest` asks for
 * `includeForm: true` and the form carries the value list the backend builds from `@UiEnumValueInfo`.
 * Never write a TypeScript label table for them.
 */
export const SM_SIGNING_PROCESS_NOTIFICATIONS_COLUMNS: { field: string, uiLabel: string }[] = [
    {field: 'n.createdDate', uiLabel: 'Zeitpunkt'},
    // See the class comment: objectId is a plain column with no joinable relationship, and no
    // platform seam turns it into a name inside a plain QL table cell.
    {field: 'e.objectId', uiLabel: 'Unterzeichner'},
    {field: 'nt.name', uiLabel: 'Benachrichtigungstyp'},
    {field: 'cct.name', uiLabel: 'Kanal'},
    {field: 'e.channelRecipientAddress', uiLabel: 'Empfängeradresse'},
    {field: 'e.recipientStatus', uiLabel: 'Status'},
    {field: 'e.errorMessage', uiLabel: 'Fehlermeldung'},
    {field: 'doc.name', uiLabel: 'Gerendertes Schreiben'}
];

/**
 * German pointer the tab MUST render (binding build constraint 4 of the capability matrix).
 *
 * It names the three refusal event types so a caseworker who does not find a refused dispatch here
 * knows exactly where it is instead. The completeness word the requirement originally carried is
 * forbidden in the Verlauf and is likewise avoided here: this sentence states what the tab holds,
 * it does not claim to hold everything.
 */
export const SM_SIGNING_PROCESS_NOTIFICATIONS_HINT =
    'Diese Übersicht zeigt Benachrichtigungen an die Unterzeichner. Meldungen an die Sachbearbeitung '
    + '– etwa „Versand verweigert“, „Erneuter Versand fehlgeschlagen“ oder „Herkunft nicht prüfbar“ – '
    + 'stehen im Verlauf, nicht hier.';

/**
 * Empty state of the tab. Deliberately not „keine Benachrichtigungen“: nothing was sent TO THE
 * UNTERZEICHNERN, which is a narrower and more honest statement.
 */
export const SM_SIGNING_PROCESS_NOTIFICATIONS_EMPTY =
    'Für diesen Signaturprozess wurde noch keine Benachrichtigung an einen Unterzeichner erzeugt.';

/** Error state of the tab. */
export const SM_SIGNING_PROCESS_NOTIFICATIONS_ERROR =
    'Die Benachrichtigungen konnten nicht geladen werden.';
